/**
 * Centred card for the sign-in, registration and password pages.
 *
 * Carries the theme and language controls, because a user who cannot read the
 * interface cannot sign in to change it -- and the language switch is the one control
 * that must be reachable before authentication (14.4).
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'

export function AuthLayout({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-text">
      <header className="flex h-14 shrink-0 items-center px-4">
        <span className="font-semibold">{t('app.name')}</span>
        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-1 text-center">
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="text-sm text-text-muted">{t('app.tagline')}</p>
          </div>
          {children}
        </div>
      </main>

      <footer className="shrink-0 p-4 text-center text-xs text-text-muted">
        {/* The non-goals and responsibility statement are a liability control, and a
            prospective user should meet them before they sign up, not after (1.2, 2.1). */}
        <a href="/privacy" className="hover:underline">
          {t('nav.privacy')}
        </a>
      </footer>
    </div>
  )
}
